/// <amd-module name="Search/Package/Components/Pivot" />

import * as React from "react";
import * as Resources from "Search/Package/Resources";
import * as Constants from "Search/Package/Constants";
import { Filter } from "SearchUI/Utilities/Filter"
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { ActionCreator } from "Search/Package/Flux/ActionCreator";
import { ActionsHub } from "Search/Package/Flux/ActionsHub";
import { getTFSPageData, ITfsPageData } from "Tfs/Platform/Page";
import { PageSource } from "Search/Shared/Base/Sources/PageSource";
import { ITabContext, IInputQuery } from "Search/Shared/Base/Types";
import { ResultsContainer } from "Search/Package/Components/Results";
import { TelemetryWriter } from "Search/Package/Flux/Sources/TelemetryWriter";
import { PackageSearchSource } from "Search/Package/Flux/Sources/PackageSearchSource";
import { StoresHub } from "Search/Package/Flux/StoresHub";
import { IVssHistoryService, INavigationStateChangedEvent } from "VSS/Platform/History";
import { getWindowTitle } from "Search/Shared/Utils";
import { ITfsNavigationService } from "Tfs/Platform/Navigation";
import { applyNavigatedUrl, updateUrl } from "Search/Package/NavigationHandler";
import { TelemetrySpy } from "Search/Package/Flux/Sources/TelemetrySpy";
import { handlePivotCommands } from "Search/Package/Components/PivotCommands";
import { IVssSearchService } from "VSS/Platform/Search";
import { handlePivotNotification } from "Search/Package/Components/Notification";
import { RelationFromExactCount } from "Search/LegacyWebApi/Contracts/Search.SharedLegacy";
import { BasePivot } from "Search/Shared/Components/BasePivot";

export class PackageSearchPivot extends BasePivot {
    private readonly actionCreator: ActionCreator;
    private readonly storesHub: StoresHub;
    private readonly pageData: ITfsPageData | undefined;
    private readonly telemetryWriter: TelemetryWriter;
    private readonly historyService: IVssHistoryService;
    private readonly telemetrySpy: TelemetrySpy;
    private readonly disposeActions: (() => void)[];

    constructor(props: ITabContext, context: IVssComponentContext) {
        super(props, Constants.EntityTypeUrlParam, context);
        this.pageData = getTFSPageData(this.context.pageContext);
        const project = this.pageData && this.pageData.project;
        const filter = new Filter({ defaultState: {}, useApplyMode: false });
        const actionsHub = new ActionsHub();

        this.telemetryWriter = new TelemetryWriter(context.pageContext, props.isTabSwitch(), props.telemetryMarker);
        this.storesHub = new StoresHub(actionsHub, context.pageContext, filter, project);
        this.actionCreator = new ActionCreator(
            filter,
            this.telemetryWriter,
            actionsHub,
            {
                searchSource: new PackageSearchSource(props.isTabSwitch(), context.pageContext),
                pageSource: new PageSource(context.pageContext)
            },
            this.storesHub.getAggregatedState,
            context.pageContext);

        this.telemetrySpy = new TelemetrySpy(this.telemetryWriter, actionsHub);
        this.historyService = this.context.pageContext.getService<IVssHistoryService>("IVssHistoryService");
        this.disposeActions = [];
    }

    public render(): JSX.Element {
        return <ResultsContainer
            actionCreator={this.actionCreator}
            storesHub={this.storesHub}
            pageContext={this.context.pageContext}
            isHosted={this.props.isHosted}
            isMember={this.props.isMember}
        />;
    }

    public componentDidMount(): void {
        super.componentDidMount();
        // Subscribe to various composite stores required for update commands, url, page title etc.
        this.subscribe();

        const routeParams = this.historyService.getState();

        // Update search input placeholder
        this.props.inputLabel.value = {
            inputPlaceholder: Resources.SearchPackagePlaceholder,
            inputAriaLabel: this.pageData && this.pageData.project ? Resources.PackageProjectInputAriaLabel : Resources.PackageOrgInputAriaLabel
        };

        // init page.
        if (routeParams.text && routeParams.text.trim()) {
            if (!this.props.isTabSwitch()) {
                this.actionCreator
                    .loadInitialState(
                        routeParams.text,
                        routeParams.filters,
                        routeParams.result,
                        routeParams.action,
                        routeParams.lp,
                        routeParams.pageSize);
            } else {
                this.actionCreator.loadInitialState(routeParams.text, undefined, undefined, undefined, undefined, routeParams.pageSize);
            }
        }
        else {
            this.actionCreator.showLandingPage();
        }

        //Update search provider for L1 search box
        const providerId = this.pageData && this.pageData.project
            ? "ms.feed.package-project-search-provider"
            : "ms.feed.package-collection-search-provider";
        this.context.pageContext.getService<IVssSearchService>("IVssSearchService").setSelectedProvider(providerId);
    }

    public componentWillUnmount(): void {
        this.disposeActions.forEach(action => action());
        this.actionCreator.dispose();
        this.storesHub.dispose();
        this.telemetrySpy.dispose();
        this.props.pivotCommands.value = { farItems: [], items: [] };
        super.componentWillUnmount();
    }

    private subscribe = (): void => {
        const searchStore = this.storesHub.getCompositeStore(["searchStore"]);
        const filterStore = this.storesHub.getCompositeStore(["filterStore"]);
        const notificationStore = this.storesHub.getCompositeStore(["notificationStore"]);

        searchStore.addChangedListener(this.onUpdateUrl);
        searchStore.addChangedListener(this.onSearchStoreUpdated);
        filterStore.addChangedListener(this.onCommandsUpdated);
        notificationStore.addChangedListener(this.onNotificationStoreChanged);

        this.props.inputQuery.subscribe(this.onSearchQueryUpdated);
        
        this.disposeActions.push(() => searchStore.removeChangedListener(this.onUpdateUrl));
        this.disposeActions.push(() => searchStore.removeChangedListener(this.onSearchStoreUpdated));
        this.disposeActions.push(() => filterStore.removeChangedListener(this.onCommandsUpdated));
        this.disposeActions.push(() => notificationStore.removeChangedListener(this.onNotificationStoreChanged));
        this.disposeActions.push(() => this.props.inputQuery.unsubscribe(this.onSearchQueryUpdated));
    }

    private onNotificationStoreChanged = (): void => {
        handlePivotNotification(this.props.pageNotification, this.storesHub.getAggregatedState, this.actionCreator);
    }

    private onCommandsUpdated = (): void => {
        handlePivotCommands(this.props.pivotCommands, this.storesHub.getAggregatedState, this.actionCreator, this.props.isMember);
    }

    private onSearchStoreUpdated = (): void => {
        const { searchStoreState } = this.storesHub.getAggregatedState();
        const { query, response } = searchStoreState;
        if (query) {
            // update title of the page.
            const title = getWindowTitle(Resources.SearchPackageTitle, query.searchText);
            this.context
                .pageContext
                .getService<ITfsNavigationService>("ITfsNavigationService")
                .setPageTitle(title);
        }

        if (response) {
            // update results count
            this.props.resultsCount[Constants.EntityTypeUrlParam].value = { count: response.count, relationFromExactCount: RelationFromExactCount.Equals };
        }
    }

    private onUpdateUrl = (): void => {
        updateUrl(this.context.pageContext, this.storesHub.getAggregatedState());
    }

    private onSearchQueryUpdated = ({ text, openInNewTab }: IInputQuery): void => {
        if (!openInNewTab && text) {
            this.actionCreator.applySearchText(text);
        } else if (text) {
            this.actionCreator.openSearchInNewTab(text);
        }
    }

    protected onNavigationOccurred = (eventData: INavigationStateChangedEvent): void => {
        applyNavigatedUrl(this.actionCreator, this.historyService.getState(), this.storesHub.getAggregatedState());
    }
}

VssComponent.register("packageSearchPivot", PackageSearchPivot);